import {
	BookOpen,
	Calendar,
	ExternalLink,
	FileText,
	HelpCircle,
	NotebookTabs,
	Palette,
} from 'lucide-react'
import { Link } from 'react-router'
import {
	EventDateStub,
	EventTimePill,
	eventRowSpineClass,
	formatEventTime,
	getRelativeTimeLabel,
} from '#app/components/event-card.tsx'
import { HomeCountdown } from '#app/components/home-countdown.tsx'
import { IosInstallTip } from '#app/components/ios-install-tip.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { safeExternalUrl } from '#app/routes/admin/evenements/+shared/evenements-config.ts'
import {
	buildExcerpt,
	loadPublicLignesDuJour,
} from '#app/routes/admin/lignes/+shared/ligne-editor.server.tsx'
import { LignesDuJourHighlight } from '#app/routes/lignes/+shared/lignes-du-jour-highlight.tsx'
import { isAppSettingEnabled } from '#app/utils/app-settings.server.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useVisibleTabs } from '#app/utils/user.ts'
import { type Route } from './+types/index.ts'

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	const serverNow = Date.now()
	if (!userId) return { isAuthenticated: false as const, serverNow }

	const [lignesDuJour, upcomingEvents, brandImagesEnabled] = await Promise.all([
		loadPublicLignesDuJour(),
		prisma.content.findMany({
			where: {
				contentType: 'Evenements',
				isPublished: true,
				visibility: true,
			},
			select: {
				id: true,
				title: true,
				content: true,
				templateData: true,
			},
			orderBy: { createdAt: 'desc' },
		}),
		isAppSettingEnabled('brand-images'),
	])

	const events = upcomingEvents
		.map((e) => {
			const td = (e.templateData ?? {}) as Record<string, unknown>
			return {
				id: e.id,
				title: e.title,
				excerpt: buildExcerpt(e.content, 200),
				url: safeExternalUrl(td.url),
				eventDate: (td.eventDate as string) ?? null,
			}
		})
		.filter((e) => e.eventDate && new Date(e.eventDate) >= new Date())
		.sort(
			(a, b) =>
				new Date(a.eventDate!).getTime() - new Date(b.eventDate!).getTime(),
		)
		.slice(0, 3)

	return {
		isAuthenticated: true as const,
		lignesDuJour,
		events,
		serverNow,
		brandImagesEnabled,
	}
}

const quickAccessSections = [
	{
		title: 'Lignes',
		href: '/lignes',
		icon: FileText,
		tone: 'primary',
		tabKey: 'lignes',
	},
	{
		title: 'Jeu de cartes',
		href: '/cartes',
		icon: NotebookTabs,
		tone: 'accent',
		tabKey: 'cartes',
	},
	{
		title: 'Engagements',
		href: '/engagements',
		icon: BookOpen,
		tone: 'secondary',
		tabKey: 'plateforme',
	},
	{ title: 'Événements', href: '/evenements', icon: Calendar, tone: 'accent' },
	{ title: 'Soutien', href: '/support', icon: HelpCircle, tone: 'muted' },
] as const

const toneMap = {
	primary: 'bg-primary/10 text-primary',
	accent: 'bg-accent/15 text-accent',
	secondary: 'bg-secondary text-secondary-foreground',
	muted: 'bg-muted text-muted-foreground',
} as const

const teaserSections = [
	{ title: 'Lignes du jour', icon: FileText },
	{ title: 'Jeu de cartes', icon: NotebookTabs },
	{ title: 'Événements', icon: Calendar },
] as const

export default function IndexRoute({ loaderData }: Route.ComponentProps) {
	const visibleTabs = useVisibleTabs()

	if (!loaderData.isAuthenticated) {
		return (
			<div className="container flex flex-1 flex-col pb-12">
				<section className="from-primary to-accent full-bleed relative overflow-hidden rounded-none bg-gradient-to-br p-6 shadow-xl md:rounded-3xl md:p-10">
					<div
						aria-hidden="true"
						className="bg-primary-foreground/10 pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full blur-3xl"
					/>
					<div
						aria-hidden="true"
						className="bg-accent/50 pointer-events-none absolute -bottom-24 -left-12 h-56 w-56 rounded-full blur-3xl"
					/>

					<div className="relative">
						<div className="text-center">
							<h1 className="text-primary-foreground text-h4">WebApp PLQ</h1>
							<p className="text-primary-foreground/70 text-body-sm mt-2">
								Vos lignes du jour, vos engagements, vos événements.
							</p>
						</div>

						<div className="mt-8 grid grid-cols-3 gap-3">
							{teaserSections.map((section) => (
								<div
									key={section.title}
									className="flex flex-col items-center gap-2 p-2"
								>
									<div className="bg-primary-foreground/15 flex h-12 w-12 items-center justify-center rounded-2xl">
										<section.icon
											className="text-primary-foreground h-5 w-5"
											aria-hidden="true"
										/>
									</div>
									<span className="text-primary-foreground/90 text-center text-xs font-medium">
										{section.title}
									</span>
								</div>
							))}
						</div>
					</div>
				</section>

				<div className="mx-auto mt-12 flex w-full max-w-md flex-col gap-3">
					<Button asChild size="lg" className="w-full">
						<Link to="/login">Se connecter</Link>
					</Button>
					<p className="text-muted-foreground text-center text-sm">
						Pas encore de compte?{' '}
						<Link
							to="/signup"
							className="text-primary font-medium underline underline-offset-4"
						>
							Activer votre compte
						</Link>
					</p>
				</div>

				<p className="text-muted-foreground mx-auto mt-auto max-w-md pt-8 text-center text-xs">
					Pas encore membre du PLQ?{' '}
					<a
						href="https://portail.plq.org/Membres/Avis2026F"
						target="_blank"
						rel="noopener noreferrer"
						className="hover:text-foreground underline underline-offset-4"
					>
						Devenir membre →
					</a>
				</p>
			</div>
		)
	}

	const { lignesDuJour, events, serverNow, brandImagesEnabled } = loaderData
	const filteredQuickAccess = quickAccessSections.filter(
		(s) => !('tabKey' in s) || visibleTabs.has(s.tabKey),
	)

	return (
		<div className="container pb-8">
			<div className="mb-8 md:mb-10">
				<HomeCountdown serverNow={serverNow} />
			</div>

			<IosInstallTip />

			{lignesDuJour && visibleTabs.has('lignes') ? (
				<div className="animate-slide-top" style={{ animationDelay: '100ms' }}>
					<LignesDuJourHighlight
						publishedAt={lignesDuJour.publishedAt}
						items={lignesDuJour.items}
					/>
				</div>
			) : null}

			{events.length > 0 ? (
				<section
					className="animate-slide-top mb-8"
					style={{ animationDelay: '150ms' }}
				>
					<div className="mb-3 flex items-center justify-between px-1">
						<h2 className="text-muted-foreground text-xs font-semibold tracking-[0.15em] uppercase">
							<Calendar
								className="-mt-0.5 mr-1.5 inline h-3.5 w-3.5"
								aria-hidden="true"
							/>
							Événements à venir
						</h2>
						<Link to="/evenements" className="text-primary text-xs font-medium">
							Tout voir
						</Link>
					</div>
					<ul className="divide-border/60 divide-y">
						{events.map((event) => {
							const date = event.eventDate ? new Date(event.eventDate) : null
							const relative = date
								? getRelativeTimeLabel(date, new Date(serverNow))
								: null
							const time = date ? formatEventTime(date) : null
							const hasExternalLink = Boolean(event.url) && !relative?.isPast
							const content = (
								<>
									{date && (
										<EventDateStub
											date={date}
											isPast={relative?.isPast}
											size="sm"
										/>
									)}
									<div className="min-w-0 flex-1">
										<div className="mb-1 flex items-center gap-2">
											{relative && (
												<EventTimePill
													label={relative.label}
													tone={relative.tone}
												/>
											)}
											{event.url && (
												<ExternalLink
													className="text-muted-foreground h-3 w-3 shrink-0"
													aria-hidden="true"
												/>
											)}
										</div>
										<p className="group-hover:text-plq-rouge line-clamp-2 text-sm leading-snug font-semibold transition-colors">
											{event.title}
										</p>
										{event.excerpt && (
											<p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs leading-snug">
												{event.excerpt}
											</p>
										)}
										{time && (
											<p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
												{time}
											</p>
										)}
									</div>
								</>
							)
							return (
								<li
									key={event.id}
									className={eventRowSpineClass(relative?.isPast)}
								>
									{hasExternalLink ? (
										<a
											href={event.url!}
											target="_blank"
											rel="noopener noreferrer"
											className="group active:bg-muted/50 flex items-start gap-3 py-3 pl-3 transition-colors"
										>
											{content}
										</a>
									) : (
										<div className="group flex items-start gap-3 py-3 pl-3">
											{content}
										</div>
									)}
								</li>
							)
						})}
					</ul>
				</section>
			) : null}

			<section
				className="animate-slide-top mb-8"
				style={{ animationDelay: '200ms' }}
			>
				<h2 className="text-muted-foreground mb-4 px-1 text-xs font-semibold tracking-[0.15em] uppercase">
					Accès rapide
				</h2>
				<div className="grid grid-cols-2 gap-3 md:grid-cols-5">
					{filteredQuickAccess.map((section) => (
						<Link
							key={section.href}
							to={section.href}
							className="flex h-28 flex-col items-center justify-center gap-3 rounded-2xl p-4 transition-transform active:scale-[0.95]"
						>
							<div
								className={`flex h-14 w-14 items-center justify-center rounded-2xl ${toneMap[section.tone]}`}
							>
								<section.icon className="h-6 w-6" aria-hidden="true" />
							</div>
							<span className="text-sm font-medium">{section.title}</span>
						</Link>
					))}
				</div>
			</section>

			{brandImagesEnabled ? (
				<section
					className="animate-slide-top"
					style={{ animationDelay: '300ms' }}
				>
					<h2 className="text-muted-foreground mb-3 px-1 text-xs font-semibold tracking-[0.15em] uppercase">
						En préparation
					</h2>
					<ul className="border-border/60 bg-muted/30 divide-border/60 divide-y rounded-2xl border-2 border-dashed">
						<li className="flex items-center gap-4 p-4">
							<div className="bg-muted flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
								<Palette
									className="text-muted-foreground h-5 w-5"
									aria-hidden="true"
								/>
							</div>
							<div className="min-w-0 flex-1">
								<h3 className="text-sm font-semibold">Image de marque</h3>
								<p className="text-muted-foreground mt-0.5 text-xs">
									Logos, couleurs et gabarits officiels
								</p>
							</div>
							<span className="bg-muted text-muted-foreground shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase">
								Bientôt
							</span>
						</li>
					</ul>
				</section>
			) : null}
		</div>
	)
}
